import debounce from 'lodash/debounce'
import isNil from 'lodash/isNil'
import { observable, runInAction } from 'mobx'
import React from 'react'
import { GroupedItems } from '../components/GroupedItems'
import ItemRenderer from '../components/ItemRenderer'
import { INDIVIDUAL_SEPARATOR_IDENTIFIER, PAGE_SEPARATOR_IDENTIFIER } from '../constants'
import { IHierarchicalItemsStore } from '../HeirarchyWiseItems'
import {
  CATEGORY_INTERNAL_SEPERATOR,
  HIERARCHY_ROOT_ID
} from '@modules/order/constants/uiConstants'
import { getEnumValue } from '@utils/get-enum-value'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { ListTableView } from '@modules/order/stores/view/List/ListViewTableService'
import { stores } from '@stores'
import { View } from '@modules/order/constants/views'

const childrenItem = (
  childrenStack: { id: string; height: number }[],
  id: string,
  end: number,
  itemHeight: number
) => {
  const stackEle =
    childrenStack.length > 1
      ? {
          id: [id, end - childrenStack.length, end - 1].join(PAGE_SEPARATOR_IDENTIFIER),
          height: itemHeight
        }
      : childrenStack.pop()
  return {
    id: stackEle.id,
    isSticky: false,
    height: stackEle.height,
    node: { id: stackEle.id }
  }
}

export class HeirarchyWiseItemsStore {
  renderer
  store: IHierarchicalItemsStore
  rootId
  rootHeight
  @observable dimensions
  @observable pageSize
  setRootHeight = (height: number) => {
    this.rootHeight = height
  }

  debouncedRecompute = debounce(() => {
    this.store.elementRefStore.ref?.recomputeTree()
  }, 20)

  resizeObserver = new ResizeObserver(entries => {
    // setTimeout(() => {
    const {
      nav: {
        queryParams: { view }
      },
      productConfiguration: { enhancedViewEnabled }
    } = stores
    let isChanged = false
    for (const entry of entries) {
      const id = (entry.target as HTMLDivElement).parentElement?.dataset?.id
      const height = entry.target?.clientHeight
      if (
        id &&
        height &&
        this.store.dimensionMapper.dimension(id).height !== height &&
        this.store.dimensionMapper.dimension(id).height + 1 !== height
      ) {
        if (
          enhancedViewEnabled &&
          this.store.dimensionMapper.dimension(id).height >= height &&
          view !== View.List
        ) {
          //do nothing
        } else {
          isChanged = true
          this.store.dimensionMapper.setDimension(id, { height: height + 1 })
        }
      }
    }
    if (isChanged) {
      this.debouncedRecompute()
    }
    // }, 900)
  })

  setRootId = rootId => {
    this.rootId = rootId
  }

  setStore = store => {
    this.store = store
  }

  setRenderer = renderer => {
    this.renderer = renderer
  }

  setDimensions = dimensions => {
    this.dimensions = dimensions
  }

  setPageSize = pageSize => {
    this.pageSize = pageSize
  }

  rowRenderer = rendererProps => {
    const { id, nodeInfo: node, style } = rendererProps || {}

    runInAction(() => {
      this.store.fetchData(id)
    })
    // display component
    return this.memoizedRenderer({ id, node, style })
  }

  memoizedRenderer = props => this.groupMemoized(props)

  groupMemoized = ({ id, node, style }) => {
    const {
      nav: {
        queryParams: { clientType, subSection }
      },
      productConfiguration: { enhancedViewEnabled }
    } = stores
    if (id.includes(PAGE_SEPARATOR_IDENTIFIER)) {
      return (
        <GroupedItems key={id} id={id} itemRenderer={this.itemRenderer} node={node} />
      )
    } else if (
      clientType === getEnumValue(VIPClientType.Internal) &&
      subSection === ListTableView.Quantity &&
      !enhancedViewEnabled // ToDo Need Confirmation for Retail Order
    ) {
      //GBB-9091: Remove/Hide activity row for retail orders
      if (id.includes(CATEGORY_INTERNAL_SEPERATOR) || id.includes(HIERARCHY_ROOT_ID)) {
        style.top = style.top > 30 ? style.top - 30 : style.top
        return this.itemRenderer({ id, node, style })
      }
    } else {
      return this.itemRenderer({ id, node, style })
    }
  }

  itemRenderer = props => this.itemMemoized(props)

  // display Hierarchy Wise component
  itemMemoized = ({ id, node, style, observe = true }) => {
    const Renderer = this.renderer
    return (
      <ItemRenderer
        node={node}
        renderer={Renderer}
        key={id}
        id={id}
        style={style}
        observe={observe}
      />
    )
  }

  getChildren = id => {
    const {
      productConfiguration: { enhancedHeaderEnabled }
    } = stores
    if (enhancedHeaderEnabled) {
      return this.getEnhancedViewChildren(id)
    }
    const {
      startIndex,
      endIndex,
      children,
      depth,
      totalItems = 0,
      isOrdersListItem = false
    } = this.store?.hierarchyDetails?.[id] || {}
    const isVisible = this.store.visibilityStore
      ? this.store.visibilityStore.isVisible(id)
      : true
    if (!isVisible) {
      return []
    }
    if (!id.includes(INDIVIDUAL_SEPARATOR_IDENTIFIER) && id !== this.rootId) {
      const hierarchyCollapsed = this.store.hierarchyCollapseStore
        ? this.store.hierarchyCollapseStore.isCollapsed(id)
        : true
      if (!hierarchyCollapsed) {
        return []
      }
    }

    if (!isNil(startIndex) && !isNil(endIndex)) {
      if (startIndex === endIndex && !children.length) {
        if (!totalItems && !isOrdersListItem) {
          return [] // GBB-11736 - returning empty array for zero products group
        }
        return [
          {
            id: `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${startIndex}`,
            isSticky: false,
            height: this.store?.dimensionMapper.dimension(
              `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${startIndex}`
            ).height,
            node: { id: `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${startIndex}` }
          }
        ]
      }

      let end = startIndex
      const childrenIds = []
      let containerWidth = this.dimensions.container.width
      const childrenStack = []
      do {
        const currentItemId = `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${end}`
        const currentItemDimension = this.store?.dimensionMapper.dimension(currentItemId)
        const currentItemWidth = currentItemDimension.width
        if (currentItemWidth && currentItemDimension?.width !== Infinity) {
          if (containerWidth - currentItemWidth > 0) {
            containerWidth = containerWidth - currentItemWidth
            childrenStack.push({ id: currentItemId, height: currentItemDimension.height })
          } else if (currentItemWidth >= containerWidth) {
            if (childrenStack.length > 0) {
              childrenIds.push(
                childrenItem(childrenStack, id, end, currentItemDimension.height)
              )
              childrenStack.length = 0
            }
            childrenStack.push({ id: currentItemId, height: currentItemDimension.height })
            containerWidth = this.dimensions.container.width - currentItemWidth
          }
        } else {
          if (childrenStack.length > 0) {
            childrenIds.push(
              childrenItem(childrenStack, id, end, currentItemDimension.height)
            )
            childrenStack.length = 0
          }
          childrenStack.push({ id: currentItemId, height: currentItemDimension.height })
        }
        end++
      } while (end - 1 !== endIndex)
      if (childrenStack.length > 0) {
        const currentItemDimension = this.store?.dimensionMapper.dimension(
          childrenStack[0].id
        )
        childrenIds.push(
          childrenItem(childrenStack, id, end, currentItemDimension.height)
        )
        childrenStack.length = 0
      }
      return childrenIds
    } else if (children?.length) {
      return children.map(childId => ({
        id: childId,
        isSticky: true,
        stickyTop:
          (this.rootHeight || 0) +
          (isNil(depth) ? 0 : depth + 1) *
            this.store?.dimensionMapper.dimension(childId).height,
        zIndex: 20 - (isNil(depth) ? 1 : depth + 2),
        height: this.store?.dimensionMapper.dimension(childId).height,
        node: { id: childId }
      }))
    } else {
      return []
    }
  }

  // This code is mainly used to return items for enhanced view
  getEnhancedViewChildren = id => {
    const {
      startIndex,
      endIndex,
      children,
      depth,
      isLeaf,
      totalItems = 0,
      isOrdersListItem = false
    } = this.store?.hierarchyDetails?.[id] || {}
    const isVisible = this.store.visibilityStore
      ? this.store.visibilityStore.isVisible(id)
      : true
    if (!isVisible) {
      return []
    }
    if (!id.includes(INDIVIDUAL_SEPARATOR_IDENTIFIER) && id !== this.rootId) {
      const hierarchyCollapsed = this.store.hierarchyCollapseStore
        ? this.store.hierarchyCollapseStore.isCollapsed(id)
        : true
      if (!hierarchyCollapsed) {
        return []
      }
    }

    if (!isNil(startIndex) && !isNil(endIndex) && isLeaf) {
      if (startIndex === endIndex && !children.length) {
        if (!totalItems && !isOrdersListItem) {
          return [] // GBB-11736 - returning empty array for zero products group
        }
        return [
          {
            id: `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${startIndex}`,
            isSticky: false,
            height: this.store?.dimensionMapper.dimension(
              `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${startIndex}`
            ).height,
            node: { id: `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${startIndex}` }
          }
        ]
      }

      let end = startIndex
      const childrenIds = []
      let containerWidth = this.dimensions.container.width
      const childrenStack = []
      do {
        const currentItemId = `${id}${INDIVIDUAL_SEPARATOR_IDENTIFIER}${end}`
        const currentItemDimension = this.store?.dimensionMapper.dimension(currentItemId)
        const currentItemWidth = currentItemDimension.width
        if (currentItemWidth && currentItemDimension?.width !== Infinity) {
          if (containerWidth - currentItemWidth > 0) {
            containerWidth = containerWidth - currentItemWidth
            childrenStack.push({ id: currentItemId, height: currentItemDimension.height })
          } else if (currentItemWidth >= containerWidth) {
            if (childrenStack.length > 0) {
              childrenIds.push(
                childrenItem(childrenStack, id, end, currentItemDimension.height)
              )
              childrenStack.length = 0
            }
            childrenStack.push({ id: currentItemId, height: currentItemDimension.height })
            containerWidth = this.dimensions.container.width - currentItemWidth
          }
        } else {
          if (childrenStack.length > 0) {
            childrenIds.push(
              childrenItem(childrenStack, id, end, currentItemDimension.height)
            )
            childrenStack.length = 0
          }
          childrenStack.push({ id: currentItemId, height: currentItemDimension.height })
        }
        end++
      } while (end - 1 !== endIndex)
      if (childrenStack.length > 0) {
        const currentItemDimension = this.store?.dimensionMapper.dimension(
          childrenStack[0].id
        )
        childrenIds.push(
          childrenItem(childrenStack, id, end, currentItemDimension.height)
        )
        childrenStack.length = 0
      }
      return childrenIds
    } else if (children?.length) {
      return children.map(childId => {
        let depthFactor = isNil(depth) ? 0 : depth + 1
        depthFactor = Math.min(depthFactor, 1)
        let stickyTop =
          (this.rootHeight || 0) +
          depthFactor * this.store?.dimensionMapper.dimension(childId).height
        return {
          id: childId,
          isSticky: true,
          stickyTop: stickyTop,
          zIndex: 20 - (isNil(depth) ? 1 : depth + 2),
          height: this.store?.dimensionMapper.dimension(childId).height,
          node: { id: childId }
        }
      })
    } else {
      return []
    }
  }

  destroy = () => {
    //this.groupMemoized?.cache?.clear?.()
    //this.itemMemoized?.cache?.clear?.()
    this.resizeObserver.disconnect()
  }
}
